import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { signalStore, withState } from '@ngrx/signals';
import { Events, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { mapResponse } from '@ngrx/operators';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { immerOn } from 'ngrx-immer/signals';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { AuthFacade } from '@kikkz/shared/auth/data-access';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import { userEvents } from '@kikkz/shared/user/util';
import { b04UserEvents } from '../events/b04-user.events';
import { B04UserService } from '../services/b04-user.service';

export interface B04UserState {
  member: boolean;
}

const initialState: B04UserState = {
  // Optimistic: member-only actions (e.g. creating a group) stay enabled until the backend says otherwise.
  member: true,
};

export const B04UserStore = signalStore(
  withState(initialState),
  withDevtools('b04-user'),
  withReducer(
    immerOn(b04UserEvents.syncUserSuccess, (state, { payload }) => {
      state.member = payload.user.member;
    }),
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      authFacade = inject(AuthFacade),
      userService = inject(B04UserService),
      logger = inject(LOGGING_SERVICE),
    ) => {
      // Gating on isAuthenticated avoids an upsert with an expired token restored from storage
      // (that sets idToken but leaves isAuthenticated=false). A token refresh yields a new
      // idToken and therefore re-upserts, same as the previous core behavior.
      const authenticatedToken$ = toObservable(
        computed(() => (authFacade.isAuthenticated() ? authFacade.idToken() : null)),
      );

      return {
        syncOnAuth$: authenticatedToken$.pipe(
          filter(Boolean),
          distinctUntilChanged(),
          switchMap(() =>
            userService.createOrUpdateUser$().pipe(
              mapResponse({
                next: (user) => b04UserEvents.syncUserSuccess({ user }),
                error: (error: unknown) => {
                  logger.error('[B04User] Failed to sync backend user', error);
                  return b04UserEvents.syncUserFailure();
                },
              }),
            ),
          ),
        ),
        // Bridge into the shared user domain: every synced b04 user also feeds the shared
        // UserStore, so `UserFacade` stays populated without the shared lib knowing anything about b04.
        bridgeToSharedUser$: events
          .on(b04UserEvents.syncUserSuccess)
          .pipe(map(({ payload }) => userEvents.loadUserSuccess({ user: payload.user }))),
      };
    },
  ),
);
